import React from "react";
import { Box, Button, ButtonBase, IconButton, Tooltip, Typography } from "@mui/material";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import AddIcon from "@mui/icons-material/Add";
import { makeStyles } from "tss-react/mui";
import { i18n } from "../../translate/i18n";
import MessageField from "./MessageField";

export const RATES = [1, 2, 3, 4, 5];

const RATE_COLORS = {
  1: { background: "#e53935", color: "#fff" },
  2: { background: "#fb8c00", color: "#fff" },
  3: { background: "#fbc02d", color: "#3a2c00" },
  4: { background: "#7cb342", color: "#fff" },
  5: { background: "#43a047", color: "#fff" },
};

const useStyles = makeStyles()((theme) => ({
  rule: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    overflow: "hidden",
  },
  ruleError: {
    borderColor: theme.palette.error.main,
  },
  ruleHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    padding: theme.spacing(1, 1.5),
    background: theme.palette.action.hover,
  },
  ruleBody: {
    padding: theme.spacing(1.5),
  },
  chips: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    flexWrap: "wrap",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
    width: 44,
    height: 26,
    borderRadius: 13,
    fontSize: 12.5,
    fontWeight: 700,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    transition: "background-color .15s, border-color .15s",
    "& svg": { fontSize: 15 },
  },
  chipLocked: {
    opacity: 0.55,
  },
  info: {
    fontSize: 18,
    color: theme.palette.text.secondary,
  },
  spacer: {
    flex: 1,
  },
  rangeError: {
    fontSize: 12,
    color: theme.palette.error.main,
    padding: theme.spacing(0, 1.5, 1),
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  uncovered: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    fontSize: 12.5,
    color: theme.palette.text.secondary,
  },
}));

export const RateChip = ({ rate, selected, locked, onClick }) => {
  const { classes, cx } = useStyles();
  const chip = (
    <ButtonBase
      className={cx(classes.chip, locked && classes.chipLocked)}
      disabled={locked || !onClick}
      onClick={onClick}
      style={selected ? { ...RATE_COLORS[rate], borderColor: RATE_COLORS[rate].background } : undefined}
    >
      {locked ? <LockOutlinedIcon /> : <StarRoundedIcon />}
      {rate}
    </ButtonBase>
  );
  if (!locked) return chip;
  return (
    <Tooltip title={i18n.t("settings.attention.survey.lockedRate")}>
      <span>{chip}</span>
    </Tooltip>
  );
};

const RateRulesEditor = ({ rules, onChange, messageLabel, uncoveredLabel, showErrors }) => {
  const { classes, cx } = useStyles();
  const uncovered = RATES.filter((rate) => !rules.some((rule) => rule.rates.includes(rate)));

  const updateRule = (index, patch) =>
    onChange(rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));

  const toggleRate = (index, rate) => {
    const { rates } = rules[index];
    const next = rates.includes(rate) ? rates.filter((r) => r !== rate) : [...rates, rate].sort((a, b) => a - b);
    updateRule(index, { rates: next });
  };

  const addRule = () => onChange([...rules, { key: `${Date.now()}-${rules.length}`, rates: [], message: "" }]);

  const removeRule = (index) => onChange(rules.filter((_, i) => i !== index));

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      {rules.map((rule, index) => {
        const lockedRates = new Set(rules.flatMap((other, i) => (i === index ? [] : other.rates)));
        const rangeMissing = showErrors && rule.rates.length === 0;
        const messageMissing = showErrors && !rule.message.trim();
        return (
          <div key={rule.key} className={cx(classes.rule, rangeMissing && classes.ruleError)}>
            <div className={classes.ruleHeader}>
              <div className={classes.chips}>
                {RATES.map((rate) => (
                  <RateChip
                    key={rate}
                    rate={rate}
                    selected={rule.rates.includes(rate)}
                    locked={lockedRates.has(rate)}
                    onClick={() => toggleRate(index, rate)}
                  />
                ))}
              </div>
              <Tooltip title={i18n.t("settings.attention.survey.rangeHint")}>
                <InfoOutlinedIcon className={classes.info} />
              </Tooltip>
              <span className={classes.spacer} />
              <Tooltip title={i18n.t("settings.attention.survey.deleteRange")}>
                <IconButton size="small" onClick={() => removeRule(index)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
            {rangeMissing && (
              <Typography className={classes.rangeError} sx={{ pt: 1 }}>
                {i18n.t("settings.attention.survey.emptyRange")}
              </Typography>
            )}
            <div className={classes.ruleBody}>
              <MessageField
                label={messageLabel}
                value={rule.message}
                onChange={(message) => updateRule(index, { message })}
                error={messageMissing}
                helperText={messageMissing ? i18n.t("settings.attention.survey.required") : undefined}
              />
            </div>
          </div>
        );
      })}
      <div className={classes.footer}>
        <Button size="small" startIcon={<AddIcon />} onClick={addRule} disabled={uncovered.length === 0}>
          {i18n.t("settings.attention.survey.addRange")}
        </Button>
        {uncovered.length > 0 && (
          <div className={classes.uncovered}>
            <span>{uncoveredLabel}</span>
            {uncovered.map((rate) => (
              <RateChip key={rate} rate={rate} />
            ))}
          </div>
        )}
      </div>
    </Box>
  );
};

export default RateRulesEditor;
